import { BatchReturn, BatchDestination, CreateBatchReturnRequest, BatchReturnStatusType } from 'returnCenter-api';
import orderAxiosInstance from './api';
import { getLanguage } from './languageStore';
import { BatchReturnStatusStatistic, BatchReturnSearchRequest, BatchReturnPageInfo } from 'types/batchReturn';

/**
 * Batch Return service - handles batch return CRUD operations
 * Maps to ReturnController batch endpoints
 */
export const batchReturnService = {
    /**
     * Get all batch destinations for the current context
     */
    getBatchDestinations: async (): Promise<BatchDestination[]> => {
        const response = await orderAxiosInstance.get<BatchDestination[]>('/api/return/batch/destinations');
        return response.data;
    },

    /**
     * Get batch returns, optionally filtered by status
     */
    getBatchReturns: async (status?: BatchReturnStatusType): Promise<BatchReturn[]> => {
        const params = status !== undefined ? { status } : {};
        const response = await orderAxiosInstance.get<BatchReturn[]>('/api/return/batch', { params });
        return response.data;
    },

    /**
     * Create a new batch return for a given destination
     */
    createBatchReturn: async (request: CreateBatchReturnRequest): Promise<BatchReturn> => {
        const response = await orderAxiosInstance.post<BatchReturn>('/api/return/batch', request);
        return response.data;
    },

    /**
     * Get batch return statistics (count per status)
     */
    getBatchReturnStatistics: async (): Promise<BatchReturnStatusStatistic[]> => {
        const response = await orderAxiosInstance.get<BatchReturnStatusStatistic[]>('/api/return/batch/statistics');
        return response.data;
    },

    /**
     * Search batch returns with paging and filtering
     */
    searchBatchReturns: async (request: BatchReturnSearchRequest): Promise<BatchReturnPageInfo> => {
        const response = await orderAxiosInstance.post<BatchReturnPageInfo>('/api/return/batch/search', request);
        return response.data;
    },

    /**
     * Get a single batch return by ID (with items)
     */
    getBatchReturnById: async (id: number): Promise<BatchReturn> => {
        const response = await orderAxiosInstance.get<BatchReturn>(`/api/return/batch/${id}`, {
            params: { language: getLanguage() },
        });
        return response.data;
    },

    /**
     * Update batch return status (e.g. Open→Closed, Closed→Sent)
     */
    updateBatchReturnStatus: async (id: number, status: BatchReturnStatusType): Promise<void> => {
        await orderAxiosInstance.put(`/api/return/batch/${id}/status`, { status });
    },

    /**
     * Receive a batch return as the destination company
     */
    receiveBatchReturn: async (id: number): Promise<void> => {
        await orderAxiosInstance.post(`/api/return/batch/${id}/receive`);
    },

    /**
     * Move rows of the sender's return to an open batch, also after their batch has been sent
     */
    moveRowsToBatch: async (batchReturnId: number, returnId: number, rowIds: number[]): Promise<void> => {
        await orderAxiosInstance.post(`/api/return/batch/${batchReturnId}/move-rows`, { returnId, rowIds });
    },
};
